import React, { useState, useCallback, useContext, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { ApiContext } from '../App';
import { toast } from 'react-toastify';

const PdfDropZone = ({ onPdfProcessed }) => {
  const API = useContext(ApiContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setIsProcessing(true);
    setError(null);
    setUploadProgress(0);
    
    // Only accept PDF files
    const file = acceptedFiles[0];
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are accepted');
      setIsProcessing(false);
      toast.error('Only PDF files are accepted');
      return;
    }
    
    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      setIsProcessing(false);
      toast.error('File size must be less than 5MB');
      return;
    }
    
    try {
      // Create form data to send the file
      const formData = new FormData();
      formData.append('cv', file);
      
      // Send the file to the backend with progress tracking
      const response = await API.post('/api/individuals/extract-cv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });
      
      const data = response.data;
      
      if (!response.data.success) {
        throw new Error(data.message || 'Failed to process PDF');
      }
      
      // Show success message with better information
      if (data.isNewIndividual) {
        toast.success(
          `New candidate "${data.individual.Name}" created successfully! CV uploaded to cloud.`,
          { autoClose: 4000 }
        );
      } else {
        toast.info(
          `Candidate "${data.individual.Name}" already exists in your database`,
          { autoClose: 3000 }
        );
      }
      
      // Call the callback with the extracted data
      onPdfProcessed(data.individual, data.isNewIndividual);
      
    } catch (err) {
      console.error('Error processing PDF:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process PDF';
      setError(errorMessage);
      
      // Show detailed error messages
      if (errorMessage.includes('cloud storage')) {
        toast.error('❌ Failed to upload CV to cloud storage. Please try again.');
      } else if (errorMessage.includes('extract')) {
        toast.error('❌ Failed to extract information from PDF. Please check the file format.');
      } else {
        toast.error(`❌ Processing failed: ${errorMessage}`);
      }
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  }, [API, onPdfProcessed]);

  // Set up manual document-level drag event listeners
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(true);
    };

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Only deactivate if we're leaving the document
      if (!e.relatedTarget || e.relatedTarget === document.body) {
        setIsDragActive(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      
      // Process the dropped files
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        onDrop(Array.from(e.dataTransfer.files));
      }
    };

    // Add event listeners to the document
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);

    // Clean up on unmount
    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, [onDrop]);

  return (
    <div
      className={`fixed inset-0 z-[9999] transition-opacity duration-300 ${
        isDragActive ? 'bg-indigo-500 bg-opacity-20 pointer-events-auto' : 'bg-transparent opacity-0 pointer-events-none'
      }`}
    >
      {isDragActive && (
        <div className="flex flex-col items-center justify-center h-full">
          <div className="bg-white p-8 rounded-lg shadow-2xl text-center max-w-lg mx-4">
            <div className="text-6xl mb-4">📄</div>
            <h2 className="text-2xl font-bold mb-2 text-indigo-700">Drop your CV here</h2>
            <p className="text-gray-600 mb-2">Information will be automatically extracted</p>
            <p className="text-sm text-indigo-600 mb-4">✨ Files are securely stored in the cloud</p>
            <div className="text-xs text-gray-500 space-y-1">
              <p>📁 Maximum file size: 5MB</p>
              <p>📄 PDF format only</p>
              <p>🔍 Auto-detects duplicates</p>
            </div>
            
            {isProcessing && (
              <div className="mt-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-700 mx-auto"></div>
                <p className="mt-2 text-indigo-600 font-medium">
                  {uploadProgress < 50 ? 'Uploading...' : 'Extracting information...'}
                </p>
                
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-3">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Processing document...</p>
                  </div>
                )}
              </div>
            )}
            
            {error && (
              <div className="mt-4 text-red-500 p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center justify-center mb-2">
                  <i className="bi bi-exclamation-triangle text-red-500 mr-2"></i>
                  <span className="font-medium">Error</span>
                </div>
                <p className="text-sm">{error}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfDropZone;